import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, onIdTokenChanged, type User } from 'firebase/auth';
import { auth } from '@/core/firebase';
import { resolveActiveOrganizationId } from '@/core/api';
import { mintOrgSession } from '@/core/sessionAuth';
import { resolvePostAuthDestination } from '@/core/workspaceBootstrap';
import { Spinner } from '@/components/primitives/Spinner';

interface AuthState {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  isAuthenticated: false,
  getToken: async () => null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    return onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    return onIdTokenChanged(auth, () => {
      const orgId = resolveActiveOrganizationId();
      if (!orgId) return;
      void mintOrgSession(orgId).catch(() => {
        /* best-effort refresh for current URL org */
      });
    });
  }, [location.pathname]);

  async function getToken(): Promise<string | null> {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** Redirect authenticated users from ``/`` (bootstrap new users, then enter workspace). */
export function HomeRedirect() {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    let cancelled = false;
    resolvePostAuthDestination()
      .then((destination) => {
        if (!cancelled) navigate(destination, { replace: true });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to start workspace');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (error) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-4 aspectly:bg-surface aspectly:px-6">
        <p className="aspectly:text-on-surface-variant aspectly:text-sm">{error}</p>
      </div>
    );
  }
  return (
    <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
      <Spinner size="lg" />
    </div>
  );
}
